// ── Google Calendar service ──────────────────────────────────────────────────
// Auth is handled by a small server-side OAuth backend (see /api/google/*):
//   • /api/google/login      — starts the consent flow (authorization-code).
//   • /api/google/callback   — stores an *encrypted refresh token* in an
//                              httpOnly cookie (never visible to the browser JS).
//   • /api/google/token      — mints a short-lived access token from it.
//   • /api/google/finish     — native-only; see connect() below.
//   • /api/google/disconnect — revokes + clears the cookie.
//
// Why a backend? Browser-only OAuth has no refresh tokens, so the connection
// dropped on every reload. With the refresh token held safely on the server, the
// connection is permanent: the browser only ever holds a short-lived access
// token in memory and asks the backend for a fresh one when it expires.
//
// SECURITY: the Client ID is public-safe. The client *secret* and the refresh
// token live only on the server (Vercel env + httpOnly cookie), never shipped to
// the browser bundle.
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const API_BASE = 'https://www.googleapis.com/calendar/v3';
const HINT_KEY = 'clarityGcalHint'; // app's signed-in email → OAuth login_hint

const isNative = Capacitor.isNativePlatform();
// The Android app's own WebView isn't the site itself (it loads bundled
// assets under https://localhost), so relative /api/... calls have nowhere
// to land — they need the real deployment origin prefixed on.
const APP_ORIGIN = process.env.REACT_APP_APP_URL || '';

function apiUrl(path) {
  return isNative ? `${APP_ORIGIN}${path}` : path;
}

// Kill switch — flip to true to pause the integration without ripping out
// the implementation (e.g. while Google's OAuth verification review is
// blocking non-owner accounts from connecting; see Google Cloud Console →
// OAuth consent screen → Verification Center).
const DISABLED = false;

// The feature is enabled when the public Client ID is present (the backend holds
// its own server-side secret). Absent → the integration stays dormant.
export const isGoogleCalendarConfigured = !DISABLED && Boolean(CLIENT_ID) && (!isNative || Boolean(APP_ORIGIN));

// Tasks-specific kill switch — paused separately from Calendar (unresolved
// sync issues; see git history around August 2026 for the debugging trail).
// Calendar stays fully live; only the Tasks half of the connection is hidden
// and skipped. The backend no longer requests the `tasks` OAuth scope at all
// (see lib/google-oauth.js) while this is off, so flip TASKS_DISABLED back to
// false there too when resuming, not just here.
const TASKS_DISABLED = true;
export const isGoogleTasksConfigured = isGoogleCalendarConfigured && !TASKS_DISABLED;

let accessToken = null;  // in-memory, short-lived bearer token
let tokenExpiry = 0;     // epoch ms when `accessToken` stops being valid

// ── Connection API (backed by /api/google/*) ─────────────────────────────────

// Remember which Google account the app is signed in as, so consent goes
// straight to that account (passed to the backend as login_hint).
export function setAccountHint(email) {
  try { if (email) localStorage.setItem(HINT_KEY, email); } catch {}
}

// Forget the remembered account. Called on sign-out so a stale hint from the
// previous Clarity account can't silently pre-select the wrong Google account
// (skipping the chooser entirely) when the next account connects.
export function clearAccountHint() {
  try { localStorage.removeItem(HINT_KEY); } catch {}
}

// Start the OAuth flow.
//
// Web: a full-page redirect to Google's consent screen. The browser navigates
// away and returns to /?gcal=connected once done. (No popup, no account
// chooser when a hint is present.)
//
// Native: Google refuses to complete OAuth inside an embedded WebView (which
// is what the Android app's WebView looks like to Google — this is the same
// reason src/capacitor/nativeGoogleAuth.js exists for sign-in), so instead we
// open the flow in the system browser via @capacitor/browser. That browser
// has its own separate cookie jar, so the callback hands the result back
// through a custom-scheme deep link instead — see useGoogleCalendar.js's
// appUrlOpen listener, which calls finishNativeConnect() below.
export function connect() {
  let hint = '';
  try { hint = localStorage.getItem(HINT_KEY) || ''; } catch {}
  const qs = new URLSearchParams();
  if (hint) qs.set('hint', hint);
  if (isNative) qs.set('platform', 'native');
  const query = qs.toString();
  const url = `${apiUrl('/api/google/login')}${query ? `?${query}` : ''}`;
  if (isNative) Browser.open({ url });
  else window.location.assign(url);
}

// Called by useGoogleCalendar's appUrlOpen handler once the system browser
// hands back the (still-encrypted) refresh token via deep link. Re-issues it
// as an httpOnly cookie directly to the app's own WebView.
export async function finishNativeConnect(rt) {
  const res = await fetch(apiUrl('/api/google/finish'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rt }),
  });
  if (!res.ok) throw new Error('finish_failed');
}

// Ask the backend for a fresh access token (it refreshes server-side). Caches it
// in memory until just before expiry. Throws (with .status) when not connected.
async function fetchToken() {
  const res = await fetch(apiUrl('/api/google/token'), { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    accessToken = null;
    tokenExpiry = 0;
    const err = new Error('not_connected');
    err.status = res.status;
    throw err;
  }
  const d = await res.json();
  accessToken = d.access_token;
  tokenExpiry = Date.now() + (Number(d.expires_in || 3600) - 60) * 1000;
  return accessToken;
}

// Shared by src/google/tasksApi.js — Tasks rides the same OAuth connection
// (same access token, now also scoped for Tasks; see lib/google-oauth.js).
export async function getValidToken() {
  if (accessToken && Date.now() < tokenExpiry) return accessToken;
  return fetchToken();
}

// Is there a working server-side connection? Hits the backend once. Used on
// mount to restore the connection with no prompt.
export async function checkConnection() {
  if (!isGoogleCalendarConfigured) return false;
  try { await fetchToken(); return true; }
  catch { return false; }
}

// Shared by src/google/tasksApi.js to force a refresh after a 401.
export function invalidateToken() {
  accessToken = null;
  tokenExpiry = 0;
}

// Quick synchronous check using the in-memory token (no network).
export function isConnected() {
  return Boolean(accessToken) && Date.now() < tokenExpiry;
}

export async function disconnect() {
  accessToken = null;
  tokenExpiry = 0;
  try { await fetch(apiUrl('/api/google/disconnect'), { method: 'POST', credentials: 'include' }); } catch { /* ignore */ }
}

// ── REST helper ──────────────────────────────────────────────────────────────

async function api(path, { method = 'GET', body, params } = {}) {
  const token = await getValidToken();
  let url = `${API_BASE}${path}`;
  if (params) {
    const qs = new URLSearchParams(params).toString();
    if (qs) url += `?${qs}`;
  }
  const res = await fetch(url, {
    method,
    cache: 'no-store',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    // Token went stale mid-flight — drop the cached one and force one
    // refresh from the backend and retry.
    accessToken = null;
    tokenExpiry = 0;
    const retryToken = await getValidToken();
    const retry = await fetch(url, {
      method,
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${retryToken}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!retry.ok) throw await toError(retry);
    return retry.status === 204 ? null : retry.json();
  }
  if (!res.ok) throw await toError(res);
  return res.status === 204 ? null : res.json();
}

async function toError(res) {
  let detail = '';
  try { detail = (await res.json())?.error?.message || ''; } catch {}
  const err = new Error(`Google Calendar API ${res.status}${detail ? `: ${detail}` : ''}`);
  err.status = res.status;
  return err;
}

// ── Calendars ────────────────────────────────────────────────────────────────

// Returns [{ id, summary, primary, backgroundColor, accessRole }]
export async function listCalendars() {
  const data = await api('/users/me/calendarList', {
    params: { minAccessRole: 'writer', showHidden: 'false' },
  });
  return (data?.items || []).map((c) => ({
    id: c.id,
    summary: c.summaryOverride || c.summary,
    primary: Boolean(c.primary),
    backgroundColor: c.backgroundColor,
    accessRole: c.accessRole,
  }));
}

// ── Events ───────────────────────────────────────────────────────────────────

// List events from one calendar within a time window. Includes cancelled
// (deleted) events so the sync engine can mirror Google-side deletions.
export async function listEvents(calendarId, { timeMin, timeMax, pageToken } = {}) {
  const params = {
    singleEvents: 'true',
    showDeleted: 'true',
    maxResults: '250',
    orderBy: 'startTime',
  };
  if (timeMin) params.timeMin = timeMin;
  if (timeMax) params.timeMax = timeMax;
  if (pageToken) params.pageToken = pageToken;
  return api(`/calendars/${encodeURIComponent(calendarId)}/events`, { params });
}

export async function insertEvent(calendarId, resource) {
  return api(`/calendars/${encodeURIComponent(calendarId)}/events`, {
    method: 'POST',
    body: resource,
  });
}

export async function patchEvent(calendarId, eventId, resource) {
  return api(`/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`, {
    method: 'PATCH',
    body: resource,
  });
}

export async function deleteEvent(calendarId, eventId) {
  try {
    await api(`/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`, {
      method: 'DELETE',
    });
  } catch (err) {
    // 404/410 → already gone on Google's side; treat as success.
    if (err.status === 404 || err.status === 410) return;
    throw err;
  }
}
